import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDungeonCrawlerStore } from '../../store/zustand/useDungeonCrawlerStore';
import { Icon } from '../../gui/Icon';
import styles from './DungeonStatsBar.module.scss';
import { humanNumber } from '@game-libs/exc-libs';

export interface DungeonStatsBarProps {
  dungeonLevel: number;
  torchRemaining: number;
  goldCollected: number;
  grayKeysCollected: number;
  hasGoldenKey: boolean;
  /** Called when user clicks "Give up"; use to close the dungeon modal. */
  onGiveUp?: () => void;
}

export const DungeonStatsBar = memo(function DungeonStatsBar({
  dungeonLevel,
  goldCollected,
  grayKeysCollected,
  hasGoldenKey,
  onGiveUp,
}: DungeonStatsBarProps) {
  const { t } = useTranslation('dungeon');
  const dungeonCrawler = useDungeonCrawlerStore((state) => state);

  return (
    <div className={styles.bar}>
      <div className={styles.sectionLeft}>
        <span>{t('found')}</span>
        <div className={styles.foundItem}>
          <Icon iconId="gold_key" large hasBackground={false} />
          <span>{hasGoldenKey ? 1 : 0}</span>
        </div>
        <div className={styles.foundItem}>
          <Icon iconId="gold_found" large hasBackground={false} />
          <span>{humanNumber(goldCollected)}</span>
        </div>
        <div className={styles.foundItem}>
          <Icon iconId="keys_found" large hasBackground={false} />
          <span>{grayKeysCollected}</span>
        </div>
      </div>
      <div className={styles.sectionCenter} title="Level">
        <span>Lvl.{dungeonLevel}</span>
      </div>
      <div className={styles.sectionRight} title="Keys owned">
        <Icon iconId="gray_key" large hasBackground={false} />
        <span>{dungeonCrawler.keys}</span>
        {onGiveUp && (
          <button
            type="button"
            className={`button small ${styles.giveUpButton}`}
            onClick={onGiveUp}
            onFocus={(e) => e.currentTarget.blur()}
            tabIndex={-1}
            title="Leave dungeon without rewards"
            data-testid="dungeon-give-up-button"
          >
            {t('giveUp')}
          </button>
        )}
      </div>
    </div>
  );
});
